import { DevRequestStatus as S, DevRequestType as T, DevRequestImpact as Im, DevRequestPriority as P, DevRequestListItem } from "../types";
import { poErr, poLayerBlocked, poFmtDate, poDate } from "./purchasing";
import { fmtDateTime } from "./shipping";
import { turkishIncludes } from "./formatters";
import { relTime } from "../components/NotificationBell";

/** Geliştirme Talepleri ekranının ortak yardımcıları (liste, çekmece, talep/işlem modalları, dosyalar, ekran seçici). */

export const DR_STATUS_LABEL: Record<S, string> = {
  [S.New]: "Yeni",
  [S.InReview]: "İnceleniyor",
  [S.NeedsInfo]: "Bilgi bekleniyor",
  [S.Planned]: "Planlandı",
  [S.InProgress]: "Geliştiriliyor",
  [S.Done]: "Tamamlandı",
  [S.Rejected]: "Reddedildi",
  [S.Withdrawn]: "Geri çekildi",
};
/** .po-st tonları. `s${status}` KULLANMA (SH_STATUS_CLASS dersi): her zaman bu eşlemeden geç. */
export const DR_STATUS_CLASS: Record<S, string> = {
  [S.New]: "s2",
  [S.InReview]: "s1",
  [S.NeedsInfo]: "s3",
  [S.Planned]: "dr-plan",
  [S.InProgress]: "dr-prog",
  [S.Done]: "s4",
  [S.Rejected]: "s5",
  [S.Withdrawn]: "dr-mute",
};
export const DR_STATUS_HINT: Record<S, string> = {
  [S.New]: "Talep kaydedildi; henüz incelenmedi.",
  [S.InReview]: "Talep değerlendiriliyor.",
  [S.NeedsInfo]: "Talep sahibinden ek bilgi bekleniyor.",
  [S.Planned]: "Yapılması kararlaştırıldı.",
  [S.InProgress]: "Üzerinde çalışılıyor.",
  [S.Done]: "Çözüldü. Sorun sürüyorsa 30 gün içinde yeniden açılabilir.",
  [S.Rejected]: "Bu talep yapılmayacak.",
  [S.Withdrawn]: "Talep sahibi talebi geri çekti.",
};

export interface DrTypeInfo { label: string; hint: string; glyph: string; hue: number; titlePh: string; descPh: string }
export const DR_TYPE: Record<T, DrTypeInfo> = {
  [T.Bug]: {
    label: "Hata", hint: "Bir şey çalışmıyor ya da yanlış sonuç veriyor", glyph: "!", hue: 0,
    titlePh: "Örn. Sevkiyat yazdırırken sayfa boş geliyor",
    descPh: "Ne yaptınız?\nNe olmasını bekliyordunuz?\nNe oldu? (hata mesajı varsa aynen yazın)\nHangi sipariş, firma ya da kayıt?",
  },
  [T.Improvement]: {
    label: "İyileştirme", hint: "Var olan bir ekran daha kolay ya da hızlı olabilir", glyph: "↑", hue: 210,
    titlePh: "Örn. Sipariş listesinde termine göre sıralama",
    descPh: "Bugün nasıl yapıyorsunuz?\nNeyin değişmesini istiyorsunuz?\nBu size ne kazandırır?",
  },
  [T.Feature]: {
    label: "Yeni özellik", hint: "Sistemde olmayan bir şeye ihtiyacım var", glyph: "+", hue: 265,
    titlePh: "Örn. Siparişleri Excel'e aktarabilmek",
    descPh: "Hangi işi yaparken ihtiyaç duyuyorsunuz?\nŞu an nasıl idare ediyorsunuz?\nNasıl çalışmasını istersiniz?",
  },
  [T.Question]: {
    label: "Soru", hint: "Bir şeyi nasıl yapacağımı bilmiyorum", glyph: "?", hue: 160,
    titlePh: "Örn. Kısmi sevkiyat nasıl yapılır?",
    descPh: "Ne yapmak istiyorsunuz? Hangi adımda takıldınız?",
  },
};
/** Tür kartları ve "Tür" süzgeci için sabit sıra. */
export const DR_TYPES: T[] = [T.Bug, T.Improvement, T.Feature, T.Question];
export const DR_IMPACT_LABEL: Record<Im, string> = { [Im.Low]: "Bekleyebilir", [Im.Medium]: "İşimi yavaşlatıyor", [Im.Blocking]: "İşimi durduruyor" };
export const DR_IMPACTS: Im[] = [Im.Low, Im.Medium, Im.Blocking];
export const DR_PRIORITY_LABEL: Record<P, string> = { [P.Low]: "Düşük", [P.Normal]: "Normal", [P.High]: "Yüksek", [P.Urgent]: "Acil" };
export const DR_PRIORITY_BADGE: Record<P, string> = { [P.Low]: "muted", [P.Normal]: "muted", [P.High]: "warn", [P.Urgent]: "err" };
/** Panelde durum/hedef tarih yerine gösterilen salt okunur not (yönetim Nedaris Destek'te). */
export const DR_VENDOR_MANAGED = "Durum ve hedef tarih Nedaris Destek tarafından güncellenir.";

export const drIsOpen = (s: S) => s <= S.InProgress;
export const DR_STEPS: [S, string][] = [[S.New, "Yeni"], [S.InReview, "İnceleniyor"], [S.Planned, "Planlandı"], [S.InProgress, "Geliştiriliyor"], [S.Done, "Tamamlandı"]];
/** Adım çubuğundaki konum (0–4). Bilgi bekleniyor "İnceleniyor" adımında durur; Reddedildi/Geri çekildi için -1 (çubuk gizlenir). */
export const drStepIndex = (s: S): number => {
  if (s === S.NeedsInfo) return 1;
  return DR_STEPS.findIndex(([x]) => x === s);
};
export const DR_STALE_DAYS = 3;     // Yeni ve destekten ses yok → "yanıtsız"
export const DR_WAIT_DAYS = 7;      // Bilgi bekleniyor bundan uzun sürerse → geç
export const DR_REOPEN_DAYS = 30;   // = sunucu kuralı
export const DR_MAX_FILES = 15, DR_MAX_TOTAL = 60 * 1024 * 1024;

// ───────────────────────────── Dosyalar ─────────────────────────────

const MB = 1024 * 1024;
export type DrFileKind = "image" | "video" | "pdf" | "file";
/** Sunucu izin listesinin (DevRequestFiles.cs) aynası: uzantı → tür / üst sınır. */
export const DR_FILE: Record<string, { kind: DrFileKind; max: number }> = {
  ".png": { kind: "image", max: 10 * MB },
  ".jpg": { kind: "image", max: 10 * MB },
  ".jpeg": { kind: "image", max: 10 * MB },
  ".webp": { kind: "image", max: 10 * MB },
  ".gif": { kind: "image", max: 10 * MB },
  ".mp4": { kind: "video", max: 20 * MB },
  ".webm": { kind: "video", max: 20 * MB },
  ".pdf": { kind: "pdf", max: 10 * MB },
  ".docx": { kind: "file", max: 10 * MB },
  ".xlsx": { kind: "file", max: 10 * MB },
  ".doc": { kind: "file", max: 10 * MB },
  ".xls": { kind: "file", max: 10 * MB },
  ".csv": { kind: "file", max: 10 * MB },
  ".txt": { kind: "file", max: 10 * MB },
};
export const DR_ACCEPT = ".png,.jpg,.jpeg,.webp,.gif,.mp4,.webm,.pdf,.docx,.xlsx,.doc,.xls,.csv,.txt";
/** Sunucunun aynı metinleri (§7); istemci önden söyler, sunucu yine denetler. */
export const DR_FILE_MSG = {
  empty: "Dosya seçin.",
  types: "İzin verilen türler: PNG, JPG, WEBP, GIF, MP4, WEBM, PDF, Word, Excel, CSV, TXT.",
  size: "Dosya en fazla 10 MB olabilir (video 20 MB).",
  count: "Bir talebe en fazla 15 dosya eklenebilir.",
  total: "Talebin dosyaları toplam 60 MB'ı aşamaz.",
  dup: "Bu dosya talepte zaten var.",
};
/** Küçük harfli uzantı, noktalı (".png"); uzantısızsa "". */
export const drExt = (name: string): string => {
  const i = (name || "").lastIndexOf(".");
  return i >= 0 ? name.slice(i).toLowerCase() : "";
};
export const drFileKind = (name: string): DrFileKind => DR_FILE[drExt(name)]?.kind ?? "file";
/** Kutucuk rozeti: "PDF", "MP4", "XLSX"…; uzantısızsa "DOSYA". */
export const drExtLabel = (name: string): string => drExt(name).slice(1).toUpperCase() || "DOSYA";
/** Tek dosya denetimi (tür + boyut). Hata metni ya da null. */
export const drCheckFile = (f: File): string | null => {
  const rule = DR_FILE[drExt(f.name)];
  if (!rule) return DR_FILE_MSG.types;
  if (f.size <= 0) return DR_FILE_MSG.empty;
  if (f.size > rule.max) return DR_FILE_MSG.size;
  return null;
};

const pad2 = (n: number) => String(n).padStart(2, "0");
const IMAGE_EXT: Record<string, string> = { "image/png": ".png", "image/jpeg": ".jpg", "image/jpg": ".jpg", "image/webp": ".webp", "image/gif": ".gif" };
/** Yapıştırılan ekran görüntüsünün adı: yerel saatle "ekran-goruntusu-20261004-143012.png" (toISOString YOK). */
export const pastedName = (d = new Date(), mime = "image/png") => {
  const ext = IMAGE_EXT[mime.toLowerCase()] || `.${(mime.split("/")[1] || "png").toLowerCase()}`;
  return `ekran-goruntusu-${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}-${pad2(d.getHours())}${pad2(d.getMinutes())}${pad2(d.getSeconds())}${ext}`;
};
/**
 * Pano içeriğindeki dosyalar (onPaste: e.clipboardData). Dosya yoksa [] döner; o zaman
 * yapıştırma normal metin olarak sürer (çağıran preventDefault yapmamalı).
 * Ekran görüntüsü gibi adsız ("image.png") görseller pastedName() ile adlandırılır;
 * Gezgin'den kopyalanan dosyalar kendi adını korur.
 */
export const drPastedFiles = (dt: DataTransfer | null | undefined): File[] => {
  if (!dt) return [];
  let files = Array.from(dt.files || []);
  if (!files.length && dt.items) {
    files = Array.from(dt.items).filter((it) => it.kind === "file").map((it) => it.getAsFile()).filter((f): f is File => !!f);
  }
  const now = new Date();
  const generic = files.filter((f) => f.type.startsWith("image/") && (!f.name || /^image\.[a-z0-9]+$/i.test(f.name))).length;
  let n = 0;
  return files.map((f) => {
    if (!f.type.startsWith("image/") || (f.name && !/^image\.[a-z0-9]+$/i.test(f.name))) return f;
    n++;
    const base = pastedName(now, f.type);
    const name = generic > 1 ? base.replace(/(\.[a-z0-9]+)$/i, `-${n}$1`) : base;
    return new File([f], name, { type: f.type, lastModified: f.lastModified || now.getTime() });
  });
};

/** Talep formundaki bekleyen dosya. `rejected` = denetimden geçmedi (yüklenmez, gerekçe `error`'da); `error` tek başına = yükleme hatası (yeniden denenebilir). */
export interface DrPending { key: string; file: File; preview?: string; progress?: number; error?: string; rejected?: boolean }
let pendingSeq = 0;
/**
 * Yeni dosyaları bekleyen listeye ekler (sürükle-bırak, dosya seçimi, Ctrl+V hepsi buradan geçer).
 * Her dosya drCheckFile + aynı dosya + 15 adet + 60 MB sınırlarıyla denetlenir; geçmeyenler
 * `rejected: true` ve gerekçesiyle listede kalır ki kullanıcı nedenini görsün.
 */
export const drMakePending = (incoming: File[], current: DrPending[]): DrPending[] => {
  const ok = current.filter((p) => !p.rejected);
  let count = ok.length;
  let total = ok.reduce((s, p) => s + p.file.size, 0);
  const sig = (f: File) => `${f.name}|${f.size}|${f.lastModified}`;
  const seen: Record<string, true> = {};
  ok.forEach((p) => { seen[sig(p.file)] = true; });
  const added = incoming.map((file): DrPending => {
    const key = `drp${++pendingSeq}`;
    const err = drCheckFile(file)
      || (seen[sig(file)] ? DR_FILE_MSG.dup : null)
      || (count + 1 > DR_MAX_FILES ? DR_FILE_MSG.count : null)
      || (total + file.size > DR_MAX_TOTAL ? DR_FILE_MSG.total : null);
    if (err) return { key, file, error: err, rejected: true };
    seen[sig(file)] = true; count++; total += file.size;
    return { key, file };
  });
  return current.concat(added);
};
/** Yüklenecekler: denetimden geçmiş, henüz başarıyla yüklenmemiş (progress 100 değil) dosyalar. */
export const drUploadable = (p: DrPending) => !p.rejected && p.progress !== 100;

export const fmtSize = (n: number) =>
  n < 1024 * 1024 ? `${Math.max(1, Math.round(n / 1024))} KB` : `${(n / 1048576).toLocaleString("tr-TR", { maximumFractionDigits: 1 })} MB`;

// ───────────────────────────── Zaman ─────────────────────────────

/** Gerçek anlar için (createdAt, statusChangedAt…): geçen tam 24 saat sayısı. */
export const daysSince = (iso: string | null | undefined) => (iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : null);
/** Yalnız tarih alanları için (targetDate): bugüne göre kalan gün (geçmişse negatif). */
export const drDaysUntil = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const t = new Date(); t.setHours(0, 0, 0, 0);
  return Math.round((poDate(iso).getTime() - t.getTime()) / 86400000);
};
const ms = (iso: string | null | undefined) => (iso ? new Date(iso).getTime() : 0);
/** Son hareketi kim yaptı: destek mi talep sahibi mi (ikisi de yoksa null). */
export const drLastBy = (r: Pick<DevRequestListItem, "lastHandlerActivityAt" | "lastRequesterActivityAt">): "Destek" | "Talep sahibi" | null => {
  if (!r.lastHandlerActivityAt && !r.lastRequesterActivityAt) return null;
  return ms(r.lastHandlerActivityAt) >= ms(r.lastRequesterActivityAt) ? "Destek" : "Talep sahibi";
};

// ───────────────────────────── Dikkat bayrakları ─────────────────────────────

/** rank: büyük = daha acil (dikkat şeridi ve tablo sırası azalan rank ile dizer). */
export interface DrFlag { key: "blocking" | "stale" | "replied" | "overdue" | "waiting"; text: string; state: "soon" | "late"; rank: number }
/**
 * §10.6 dikkat kuralları. handler = görüntüleyen talepleri yönetiyor (dev-requests:update).
 * Yönetenler: işi durduran, yanıtsız, talep sahibi yanıtladı, hedef tarihi geçen.
 * Diğerleri: yalnız kendi "Bilgi bekleniyor" talepleri.
 * Dönen dizi rank'e göre azalan sıradadır.
 */
export const drFlags = (r: DevRequestListItem, handler: boolean): DrFlag[] => {
  const out: DrFlag[] = [];
  const open = drIsOpen(r.status);
  if (handler) {
    if (open && r.impact === Im.Blocking && (r.status === S.New || !r.lastHandlerActivityAt))
      out.push({ key: "blocking", text: "İşi durduruyor", state: "late", rank: 5 });
    const age = daysSince(r.createdAt) ?? 0;
    if (r.status === S.New && !r.lastHandlerActivityAt && age >= DR_STALE_DAYS) {
      const late = age >= DR_WAIT_DAYS;
      out.push({ key: "stale", text: `${age} gündür yanıtsız`, state: late ? "late" : "soon", rank: late ? 4 : 1 });
    }
    if (open && r.status !== S.New && r.lastRequesterActivityAt
      && ms(r.lastRequesterActivityAt) > ms(r.lastHandlerActivityAt ?? r.createdAt))
      out.push({ key: "replied", text: `Talep sahibi yanıtladı · ${relTime(r.lastRequesterActivityAt)}`, state: "soon", rank: 2 });
    if (r.targetDate && (r.status === S.Planned || r.status === S.InProgress)) {
      const left = drDaysUntil(r.targetDate) ?? 0;
      if (left < 0) out.push({ key: "overdue", text: `Hedef tarih ${-left} gün geçti`, state: "late", rank: 3 });
    }
  } else if (r.isMine && r.status === S.NeedsInfo) {
    const late = (daysSince(r.statusChangedAt) ?? 0) >= DR_WAIT_DAYS;
    out.push({ key: "waiting", text: `Bilgi istendi · ${relTime(r.statusChangedAt)}`, state: late ? "late" : "soon", rank: late ? 3 : 2 });
  }
  return out.sort((a, b) => b.rank - a.rank);
};
/** Bayrakların en yüksek rank'i (yoksa 0) — sıralama anahtarı. */
export const drTopRank = (flags: DrFlag[]) => flags.reduce((m, f) => Math.max(m, f.rank), 0);

// ───────────────────────────── Durum geçişleri ─────────────────────────────

/**
 * Düğme/başlık metni (§10.7). Geçişin kendisine izin verilip verilmediğini sunucu söyler
 * (actions.transitions; panelde yalnız talep sahibinin geri çekme / yeniden açması); bu yalnız etiket üretir.
 */
export const drTransitionLabel = (from: S, to: S): string => {
  if (to === S.Withdrawn) return "Geri çek";
  if (to === S.InReview && !drIsOpen(from)) return "Yeniden aç";
  return DR_STATUS_LABEL[to];
};

// ───────────────────────────── Arama / kişi ─────────────────────────────

/** Çok terimli arama: no, başlık, özet, ekran, talep eden ve tür adı. */
export const drMatches = (r: DevRequestListItem, q: string) => {
  const terms = q.trim().split(/\s+/).filter(Boolean);
  if (!terms.length) return true;
  const hay = [r.number, r.title, r.snippet, r.screenName, r.requesterName, DR_TYPE[r.type]?.label].filter(Boolean).map(String);
  return terms.every((t) => hay.some((h) => turkishIncludes(h, t)));
};
/** "Ayşe Yılmaz" → "AY" (yazışma başlığındaki küçük işaret). */
export const drInitials = (name: string) =>
  (name || "").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toLocaleUpperCase("tr-TR") || "?";
/** Ada göre sabit renk tonu (--po-hue). */
export const drHue = (s: string) => { let h = 0; for (const ch of s || "") h = (h * 31 + ch.charCodeAt(0)) % 360; return h; };
/** "Ayşe Yılmaz" → "Ayşe Y."; e-posta ve tek kelime olduğu gibi kalır.
 *  " · " eki korunur, yalnız sol taraf kısalır: "Ayşe Yılmaz · Nedaris Destek" → "Ayşe Y. · Nedaris Destek". */
export const drShortName = (name: string): string => {
  const n = (name || "").trim();
  const i = n.indexOf(" · ");
  if (i > 0) return drShortName(n.slice(0, i)) + n.slice(i);
  const w = n.split(/\s+/).filter(Boolean);
  if (w.length < 2 || n.includes("@")) return n;
  return `${w.slice(0, -1).join(" ")} ${w[w.length - 1][0].toLocaleUpperCase("tr-TR")}.`;
};

export const drErr = poErr;
export const drLayerBlocked = poLayerBlocked;
export const drFmtDate = poFmtDate;          // yalnız tarih: targetDate, closedAt (gün olarak)
export const drFmtDateTime = fmtDateTime;    // gerçek anlar: createdAt, olaylar, updatedAt
export { poDate };
